import router from "@/router";
import { IStatus } from "@/types";

export enum CellsType {
    Route = 'route',
    Number = 'number',
    Date = 'date',
    Status = 'status',
    Text = 'text',
    Boolean = 'boolean',
    Uniq = 'uniq'
}

type ComponentDataMap = {
    [CellsType.Boolean]: BooleanCellData;
    [CellsType.Text]: TextCellData;
    [CellsType.Route]: RouteCellData;
    [CellsType.Date]: DateCellData;
    [CellsType.Number]: NumberCellData;
    [CellsType.Status]: StatusCellData;
    [CellsType.Uniq]: any;
}

export type CellType = typeof CellsType[keyof typeof CellsType];

type Cell<ComponentData = any> = {
    componentData: ComponentData;
    type?: CellType;
}

export type RawConfig<Item> = {
    [K in CellsType]?: {
        [P in keyof Item]?: ComponentDataMap[K]
    }
}
export type CellConfig<Item> = {
    [K in keyof Item]: {
        type: CellType,
        data: ComponentDataMap[CellType]
    }
}
export type FormattedItem<Item> = {
    [K in keyof Item]: Cell;
}

const createConfig = <Item>(rawConfig: RawConfig<Item>, columns?: string[]): CellConfig<Item> => {
    columns = columns ?? router.currentRoute.query.columns as string[];

    const config = Object.entries(rawConfig).reduce((acc, configsByType) => {
        const type = configsByType[0];
        const configs = configsByType[1];
        Object.entries(configs).forEach(([key, data]) => {
            if (columns && columns.includes(key)) {
                acc[key] = { type, data };
            }
        });
        return acc;
    }, {} as CellConfig<Item>);

    if (columns && Array.isArray(columns)) {
        const configsItems = Object.values(rawConfig).flatMap(obj => Object.keys(obj));
        const notExistingConfigs = columns.filter(i => !configsItems.includes(i));
        notExistingConfigs.forEach(key => {
            config[key] = { type: CellsType.Text, data: {} };
        });
    }

    return config;
};

export const prepareItems = <Item extends Record<string, any>>(
    items: Item[],
    rawConfig: RawConfig<Item>,
    columns?: string[],
): FormattedItem<Item>[] => {
    const config = createConfig(rawConfig, columns);
    const result = new Array(items.length) as FormattedItem<Item>[];

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const transformed = {} as FormattedItem<Item>;

        for (const key in config) {
            const { type, data } = config[key];
            transformed[key] = { type, componentData: { data, item, value: item[key] } };
        }
        result[i] = transformed;
    }

    return result;
};

// -- Route cell
export interface RouteCellData<Item = any, K extends keyof Item = any> {
    item?: Item;
    value?: Item[K];
    valueKey?: string,
    customParams?: (i: Item) => { [key: string]: string },
    name: ((i: Item) => string) | string,
    text?: ((i: Item) => string) | string,
    isChip?: ((i: Item) => boolean) | boolean,
    isDisabled?: boolean,
    tooltip?: ((i: Item) => string) | string,
    isCopyTooltip?: boolean;
    icon?: ((i: Item) => SVGElement) | SVGElement,
    iconIndent?: number,
    query?: ((i: Item) => { [key: string]: any }) | string,
    isTextCenter?: boolean,
    align?: 'left' | 'center' | 'right',
    isMasterChip?: ((i: Item) => boolean) | boolean,
}

// -- Boolean cell
export interface BooleanCellData<Item = any, K extends keyof Item = any> {
    value?: Item[K];
    trueValue?: string;
    falseValue?: string;
    isIcon?: boolean;
    trueIcon?: SVGElement;
    falseIcon?: SVGElement
    isTooltip?: boolean;
    trueTooltip?: string;
    falseTooltip?: string;
    trueColor?: 'red' | 'green';
    falseColor?: 'red' | 'green';
}

// -- Text cell
export interface TextCellData<Item = any, K extends keyof Item = any> {
    item?: Item;
    value?: Item[K];
    color?: ((i: Item) => 'red' | undefined) | 'red';
    template?: (i: Item) => string;
    clamp?: number;
    tooltip?: ((i: Item) => string) | string;
    tooltipWidth?: string;
    icon?: ((i: Item) => SVGElement) | SVGElement;
    isCopyOnClick?: boolean;
    isLink?: boolean;
    isWordBreakAll?: boolean;
    textAlign?: string;
}

// -- Number cell
export interface NumberCellData<Item = any, K extends keyof Item = any> {
    value?: ((value: Item) => Item[K]) | Item[K];
    isMono?: boolean;
    noWrap?: boolean;
    currency?: ((item: Item) => string) | string;
    digits?: number;
    isSymbolCurrency?: boolean;
    isSymbolSign?: boolean;
    color?: ((i: Item) => 'red' | 'green' | undefined) | 'red' | 'green';
}

// -- Date cell
export interface DateCellData<Item = any, K extends keyof Item = any> {
    value?: Item[K];
    isLine?: boolean;
    isHideTime?: boolean;
    isSeconds?: boolean;
    isShowWeekDay?: boolean;
    format?: string;
}

// -- Status cell
export interface StatusCellData {
    translate?: ((status: string | boolean) => IStatus) | IStatus;
    disableHover?: boolean;
}
